import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Check, ChevronsUpDown, Printer, Save, User, Calendar as CalendarIcon, Clock, Eye, Loader2 } from "lucide-react";
import { commonReasons as defaultReasons } from "../mock";
import { usePasses } from "../context/PassesContext";
import { useTalmidim } from "../context/TalmidimContext";
import { useSettings } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import ReceiptView from "../components/ReceiptView";
import { normalizeReasons, groupReasons, getReasonCategory, CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED, CATEGORY_LABEL_HE } from "../lib/reasons";

// Fallback shortcuts if settings not loaded yet
const FALLBACK_SHORTCUTS = ["פארשלאפן", "דאקטער אפאינטמענט", "טראנספארט פראבלעם", "משפחה עמערזענסי"];

export default function NewPass() {
  const navigate = useNavigate();
  const { addPass } = usePasses();
  const { talmidim } = useTalmidim();
  const { settings } = useSettings();
  const { user } = useAuth();
  const { toast } = useToast();

  const REASON_SHORTCUTS = settings?.reasonShortcuts?.length ? settings.reasonShortcuts : FALLBACK_SHORTCUTS;
  const rawReasons = settings?.commonReasons?.length ? settings.commonReasons : defaultReasons;
  const commonReasons = normalizeReasons(rawReasons).map((r) => r.text);
  const reasonsByCategory = groupReasons(rawReasons);
  const adminName = user?.name || settings?.adminName || "";

  const now = new Date();
  const [talmidId, setTalmidId] = useState("");
  const [comboOpen, setComboOpen] = useState(false);
  const [date, setDate] = useState(now.toISOString().slice(0, 10));
  const [time, setTime] = useState(now.toTimeString().slice(0, 5));
  const [reasonPreset, setReasonPreset] = useState("");
  const [reasonCustom, setReasonCustom] = useState("");
  const [categoryOverride, setCategoryOverride] = useState(null); // null | "justified" | "unjustified"
  const [saving, setSaving] = useState(false);

  const selectedTalmid = useMemo(
    () => talmidim.find((t) => String(t.id) === String(talmidId)),
    [talmidId, talmidim]
  );

  const finalReason = (reasonCustom || reasonPreset || "").trim();
  // Category: user override wins, else look up in settings list
  const inferredCategory = getReasonCategory(finalReason, rawReasons);
  const finalCategory = categoryOverride || inferredCategory || null;

  const canSubmit = selectedTalmid && date && time && (finalReason || "").trim().length > 0 && !saving;

  const previewPass = useMemo(() => {
    if (!selectedTalmid) return null;
    return {
      id: "LP-••••••",
      talmidName: selectedTalmid.fullName,
      fatherName: selectedTalmid.fatherName,
      klasName: selectedTalmid.klasName,
      date,
      time,
      reason: finalReason,
      reasonCategory: finalCategory,
      signedBy: adminName,
      createdAt: new Date().toISOString(),
    };
  }, [selectedTalmid, date, time, finalReason, finalCategory, adminName]);

  const pickShortcut = (label) => {
    setReasonPreset(label);
    setReasonCustom(label);
  };

  const resetForm = () => {
    setTalmidId("");
    setReasonPreset("");
    setReasonCustom("");
    setCategoryOverride(null);
    const n = new Date();
    setDate(n.toISOString().slice(0, 10));
    setTime(n.toTimeString().slice(0, 5));
  };

  const submit = async (thenPrint) => {
    if (!canSubmit) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const pass = await addPass({
        talmidId: selectedTalmid.id,
        talmidName: selectedTalmid.fullName,
        fatherName: selectedTalmid.fatherName,
        klasId: selectedTalmid.klasId,
        klasName: selectedTalmid.klasName,
        date,
        time,
        reason: finalReason,
        reasonCategory: finalCategory,
        signedBy: adminName,
      });
      toast({ title: "Pass saved!", description: `${pass.id} · ${pass.talmidName}` });
      if (thenPrint) {
        // Open print in a new tab and stay on this page (form is reset) so
        // the office can create the next pass immediately.
        window.open(`/print/${pass.id}`, "_blank");
        resetForm();
      } else {
        navigate("/history");
      }
    } catch (e) {
      toast({ title: "Error", description: e?.response?.data?.detail || "Something went wrong", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="font-serif-heb text-3xl font-bold text-slate-900">New Late Pass</h1>
        <p className="text-slate-500 text-sm mt-1">Create a late pass · Prints on 80mm thermal receipt</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6">
        {/* FORM */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-6 lg:p-8 space-y-5">
            {/* STUDENT */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                Student
              </label>
              <Popover open={comboOpen} onOpenChange={setComboOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between h-11 font-normal"
                  >
                    {selectedTalmid ? (
                      <span className="flex items-center gap-2">
                        <span className="font-medium" dir="rtl">{selectedTalmid.fullName}</span>
                        <span className="text-xs text-slate-500" dir="rtl">· {selectedTalmid.klasName}</span>
                      </span>
                    ) : (
                      <span className="text-slate-400">Select a student...</span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search student..." />
                    <CommandList>
                      <CommandEmpty>No student found</CommandEmpty>
                      <CommandGroup>
                        {talmidim.map((t) => (
                          <CommandItem
                            key={t.id}
                            value={`${t.fullName} ${t.klasName}`}
                            onSelect={() => {
                              setTalmidId(String(t.id));
                              setComboOpen(false);
                            }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${String(talmidId) === String(t.id) ? "opacity-100" : "opacity-0"}`} />
                            <div className="flex-1" dir="rtl">
                              <div className="font-medium">{t.fullName}</div>
                              <div className="text-xs text-slate-500">{t.klasName}</div>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {selectedTalmid && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mt-2 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-slate-500">Student</div><div className="font-medium" dir="rtl">{selectedTalmid.fullName}</div></div>
                  <div><div className="text-xs text-slate-500">Class</div><div className="font-medium" dir="rtl">{selectedTalmid.klasName}</div></div>
                </div>
              )}
            </div>

            {/* DATE / TIME */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-slate-400" />
                  Date
                </label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 font-en" />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  Arrival Time
                </label>
                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-11 font-en" />
              </div>
            </div>

            {/* REASON SHORTCUTS */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 flex items-center justify-between">
                <span>Reason</span>
                <span className="text-xs text-slate-400 font-normal">Quick-tap or choose ↓</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {REASON_SHORTCUTS.map((r) => {
                  const active = finalReason === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => pickShortcut(r)}
                      dir="rtl"
                      className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                        active
                          ? "bg-blue-600 text-white border-blue-600 shadow-md scale-[1.02]"
                          : "bg-white text-slate-700 border-slate-200 hover:bg-blue-50 hover:border-blue-300"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
              <Select value={reasonPreset} onValueChange={(v) => { setReasonPreset(v); setReasonCustom(v); }}>
                <SelectTrigger className="h-11 mt-2">
                  <SelectValue placeholder="Or select from the list..." />
                </SelectTrigger>
                <SelectContent>
                  {[CATEGORY_JUSTIFIED, CATEGORY_UNJUSTIFIED].map((cat) => {
                    const items = reasonsByCategory[cat];
                    if (!items.length) return null;
                    return (
                      <div key={cat} className="py-1">
                        <div className="px-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500" dir="rtl">
                          {CATEGORY_LABEL_HE[cat]}
                        </div>
                        {items.map((r) => (
                          <SelectItem key={r.text} value={r.text}>
                            <span dir="rtl">{r.text}</span>
                          </SelectItem>
                        ))}
                      </div>
                    );
                  })}
                </SelectContent>
              </Select>

              <Textarea
                value={reasonCustom}
                onChange={(e) => { setReasonCustom(e.target.value); setReasonPreset(""); }}
                placeholder="…אָדער שרייבט אָן א סיבה"
                rows={2}
                dir="rtl"
                className="mt-2"
                data-testid="reason-textarea"
              />

              {/* Category picker — always shown so custom reasons can be tagged too */}
              {finalReason && (
                <div className="pt-2">
                  <div className="text-xs font-medium text-slate-600 mb-1.5 flex items-center justify-between">
                    <span>Reason Category</span>
                    {inferredCategory && !categoryOverride && (
                      <span className="text-[10px] text-slate-400 font-normal">
                        Auto-detected — click to change
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: CATEGORY_JUSTIFIED, color: "emerald" },
                      { key: CATEGORY_UNJUSTIFIED, color: "red" },
                    ].map(({ key, color }) => {
                      const active = finalCategory === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setCategoryOverride(key)}
                          dir="rtl"
                          data-testid={`category-${key}`}
                          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all flex items-center justify-center gap-2 ${
                            active
                              ? color === "emerald"
                                ? "bg-emerald-600 text-white border-emerald-600 shadow-md"
                                : "bg-red-600 text-white border-red-600 shadow-md"
                              : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${
                            active ? "bg-white" : color === "emerald" ? "bg-emerald-500" : "bg-red-500"
                          }`} />
                          {CATEGORY_LABEL_HE[key]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* SIGNATURE */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Signed by</label>
              <Input value={adminName} readOnly className="h-11 bg-slate-50" dir="rtl" />
            </div>

            {/* ACTIONS */}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-11" onClick={() => submit(false)} disabled={!canSubmit}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Only
              </Button>
              <Button className="flex-1 h-11 gradient-blue-mild text-white" onClick={() => submit(true)} disabled={!canSubmit}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                Save & Print
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* LIVE PREVIEW */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="text-sm font-medium text-slate-700 flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-blue-600" />
            Live Preview
            <span className="text-xs text-slate-400 font-normal">80mm receipt</span>
          </div>
          <div className="p-4 bg-gradient-to-br from-slate-100 to-slate-50 rounded-2xl border border-slate-200">
            <ReceiptView pass={previewPass} previewMode />
          </div>
        </div>
      </div>
    </div>
  );
}
